import React, { useState } from 'react';

const ConsentDropdownStep = ({ question, value, onAnswer, onNext, onDisqualify }) => {
  const [selectedOption, setSelectedOption] = useState(value?.option || value || '');
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState('');
  const [checkboxChecked, setCheckboxChecked] = useState(value?.checkboxChecked || false);

  // Check if this question requires a checkbox
  const hasCheckbox = question.checkbox !== undefined;

  // Selecting an option only records the choice — it never disqualifies here.
  // Disqualification is deferred to Continue so a stray/misread tap on the
  // "do not wish to continue" option (which sits right under the affirmative
  // one and opens with "I have read the above information…") is recoverable:
  // the user sees the warning box and can switch back to acknowledge instead of
  // being instantly kicked to the "We're Sorry" screen.
  const handleSelect = (optionValue) => {
    setSelectedOption(optionValue);
    setError('');
  };

  const handleContinue = () => {
    if (!selectedOption) {
      setError('Please select an option to continue');
      return;
    }

    // If checkbox is required, check if it's checked
    if (hasCheckbox && !checkboxChecked) {
      setError('Please accept the terms to continue');
      return;
    }

    const selectedOpt = question.options.find(opt => opt.value === selectedOption);
    // Disqualifying option confirmed via Continue → now trigger disqualification.
    if (selectedOpt?.disqualifies) {
      onAnswer(selectedOption);
      if (onDisqualify) {
        onDisqualify(`Based on your response to "${question.question}", you may not be eligible for this treatment.`);
      }
      return;
    }

    // Store both the option and checkbox state if checkbox exists
    if (hasCheckbox) {
      onAnswer({ option: selectedOption, checkboxChecked });
    } else {
      onAnswer(selectedOption);
    }
    onNext();
  };

  // Render checkbox text with hyperlinks
  const renderCheckboxText = () => {
    if (!question.checkbox) return null;
    
    const { text, links } = question.checkbox;
    
    // Replace link placeholders with actual hyperlinks
    let parts = [text];
    
    if (links && links.length > 0) {
      links.forEach(link => {
        const newParts = [];
        parts.forEach(part => {
          if (typeof part === 'string') {
            const splitParts = part.split(link.text);
            splitParts.forEach((splitPart, idx) => {
              if (splitPart) newParts.push(splitPart);
              if (idx < splitParts.length - 1) {
                newParts.push(
                  <a
                    key={link.text}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#2d8a5f] hover:underline"
                  >
                    {link.text}
                  </a>
                );
              }
            });
          } else {
            newParts.push(part);
          }
        });
        parts = newParts;
      });
    }
    
    return parts;
  };

  return (
    <div data-testid={`step-${question.id}`}>
      {/* Question */}
      <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-900 mb-4">
        {question.question}
      </h2>

      {/* Consent Text Box */}
      {question.consentText && (
        <div className="mb-6">
          <div 
            className={`bg-gray-50 rounded-xl p-4 border border-gray-200 transition-all ${
              expanded ? 'max-h-[500px] overflow-y-auto' : 'max-h-[200px] overflow-hidden'
            }`}
          >
            <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
              {question.consentText}
            </div>
          </div>
          {question.consentText.length > 500 && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="mt-2 text-sm text-[#2d8a5f] hover:underline flex items-center gap-1"
            >
              {expanded ? (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                  Show less
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  Read more
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Dropdown Selection */}
      <div className="space-y-3 mb-6">
        {question.options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleSelect(option.value)}
            className={`w-full p-4 rounded-xl text-left transition-all border-2 ${
              selectedOption === option.value
                ? option.disqualifies 
                  ? 'border-orange-400 bg-orange-50'
                  : 'border-[#2d8a5f] bg-[#f0faf4]'
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
            data-testid={`consent-option-${index}`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedOption === option.value
                  ? option.disqualifies
                    ? 'border-orange-500 bg-orange-500'
                    : 'border-[#2d8a5f] bg-[#2d8a5f]'
                  : 'border-gray-300'
              }`}>
                {selectedOption === option.value && (
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <span className={`text-sm font-medium ${
                option.disqualifies && selectedOption === option.value
                  ? 'text-orange-700'
                  : 'text-gray-800'
              }`}>
                {option.label}
              </span>
            </div>
          </button>
        ))}
      </div>

      {error && (
        <p className="text-red-500 text-sm text-center mb-4">{error}</p>
      )}

      {/* Warning if decline selected */}
      {selectedOption && question.options.find(o => o.value === selectedOption)?.disqualifies && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-orange-700">
              By selecting this option, you will not be able to proceed with this treatment. Please consult with your healthcare provider for alternative options.
            </p>
          </div>
        </div>
      )}

      {/* Checkbox with hyperlinks (optional) */}
      {hasCheckbox && (
        <div className="mb-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={checkboxChecked}
              onChange={(e) => {
                setCheckboxChecked(e.target.checked);
                setError('');
              }}
              className="w-5 h-5 rounded border-gray-300 text-[#2d8a5f] focus:ring-[#2d8a5f] mt-0.5 flex-shrink-0"
              data-testid="consent-checkbox"
            />
            <span className="text-sm text-gray-700">
              {renderCheckboxText()}
            </span>
          </label>
        </div>
      )}

      <button
        onClick={handleContinue}
        className="btn-primary"
        data-testid="continue-button"
      >
        Continue
      </button>
    </div>
  );
};

export default ConsentDropdownStep;
